/**
 * Dragging an asset card out of the drawer: onto a folder (`data-asset-drop-source`,
 * `data-asset-drop-folder`) moves it there (same source) or copies it (another source); onto the
 * graph it makes a node attached to it (the Add node window opens where it was dropped, for a
 * node at that spot of the view shown); onto an element taking files (`data-asset-target`: the
 * board, a document) it is told to that element (`ASSET_DROP`).
 */
import type { AssetInfo } from "@protocol/AssetInfo";
import type { AssetSource } from "@protocol/AssetSource";
import { toast } from "../../shared/toast";
import { ASSET_DROP, type AssetDrop } from "../../shared/assets";
import { spotAt, type Spot } from "../../shared/spot";

/** What a drag reports. */
export interface AssetDragHooks {
  /** Where the ghost is (page point), or `null` when the drag ends. */
  ghost(state: { asset: AssetInfo; x: number; y: number } | null): void;
  /** Dropped on the graph, at this spot and page point. */
  drop(spot: Spot, page: { x: number; y: number }): void;
  /** Dropped on a folder of the drawer. */
  toFolder(source: AssetSource, folder: string): void;
}

/** How far (in pixels) the pointer must move before a press becomes a drag. */
const DRAG_THRESHOLD = 6;

/** Follows a press on the card of `asset`, kept in `source` (mouse and pen). */
export function dragAsset(e: PointerEvent, source: AssetSource, asset: AssetInfo, hooks: AssetDragHooks): void {
  if (e.pointerType === "touch" || e.button !== 0) return;
  const [x0, y0] = [e.clientX, e.clientY];
  let moved = false;
  /** Shows the ghost once the press moved far enough. */
  const move = (ev: PointerEvent) => {
    if (!moved && Math.hypot(ev.clientX - x0, ev.clientY - y0) > DRAG_THRESHOLD) moved = true;
    if (moved) hooks.ghost({ asset, x: ev.clientX, y: ev.clientY });
  };
  /** Ends the drag where it was dropped. */
  const up = (ev: PointerEvent) => {
    removeEventListener("pointermove", move);
    removeEventListener("pointerup", up);
    hooks.ghost(null);
    if (!moved) return;
    addEventListener("click", (c) => c.stopPropagation(), { capture: true, once: true });
    const under = document.elementFromPoint(ev.clientX, ev.clientY);
    const folder = under?.closest<HTMLElement>("[data-asset-drop-source]");
    if (folder) return hooks.toFolder(folder.dataset.assetDropSource as AssetSource, folder.dataset.assetDropFolder ?? "");
    // The board makes a picture of it where it was dropped (`board/BoardView.tsx`), a document puts it in its text.
    const target = under?.closest("[data-asset-target]");
    if (target) return void target.dispatchEvent(new CustomEvent<AssetDrop>(ASSET_DROP, { detail: { source, path: asset.path, x: ev.clientX, y: ev.clientY } }));
    const spot = spotAt(ev.clientX, ev.clientY);
    if (!spot) return void toast("Drop the file on the graph to make a node of it, or on a folder to move it.");
    hooks.drop(spot, { x: ev.clientX, y: ev.clientY });
  };
  addEventListener("pointermove", move);
  addEventListener("pointerup", up);
}
